// src/pages/admin/common.js
// Shared pieces of the superadmin panel: status badges, the action dialog and the
// user detail modal. Every action goes through adminAPI (backend routes/admin.js).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI } from '../../utils/api';
import { useStore } from '../../store';

export const errorOf = (err, fallback) => err.response?.data?.error || fallback;
export const fmtDate = (d) => (d ? new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const fmtDay = (d) => (d ? new Date(d).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—');

export const STATUS_LABEL = { pending: 'Pending', active: 'Active', suspended: 'Suspended', deactivated: 'Deactivated', rejected: 'Rejected' };

export function StatusBadge({ user }) {
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      <span className={`badge badge-${user.status}`}>{STATUS_LABEL[user.status] || user.status}</span>
      {!user.email_verified_at && <span className="badge badge-unverified">Email unverified</span>}
    </span>
  );
}

// Audit-log wording (admin_actions.action)
export const ACTION_LOG_LABEL = {
  approve: 'Approved', reject: 'Rejected', suspend: 'Suspended', deactivate: 'Deactivated',
  reactivate: 'Reactivated', logout_all: 'Signed out of all devices', verify_email: 'Marked email as verified',
  resend_verification: 'Resent verification email', delete: 'Deleted',
  org_approve: 'Approved organization', org_reject: 'Rejected organization', org_suspend: 'Suspended organization',
  org_deactivate: 'Deactivated organization', org_reactivate: 'Reactivated organization', org_delete: 'Deleted organization',
};

// What the superadmin can do to a user, by action id (= API path segment).
// reason: null (none) | 'optional' | 'required'
export const ACTIONS = {
  approve: {
    label: 'Approve', btn: 'btn-primary', reason: null,
    describe: (u) => (u.email_verified_at
      ? `${u.name} will be able to sign in and will get an email saying so.`
      : `${u.name} hasn't verified their email yet, so they still can't sign in until they do. They'll get the approval email now.`),
  },
  reject: {
    label: 'Reject', btn: 'btn-danger', reason: 'optional',
    describe: (u) => `${u.name} won't be able to sign in. They'll get an email, including the reason if you give one. You can still approve them later.`,
  },
  suspend: {
    label: 'Suspend', btn: 'btn-danger', reason: 'required',
    describe: (u) => `${u.name} is signed out everywhere, can't sign in, and their scheduled posts are paused until you reactivate them. They'll get an email with the reason.`,
  },
  deactivate: {
    label: 'Deactivate', btn: 'btn-outline', reason: 'optional',
    describe: (u) => `${u.name} is signed out everywhere, can't sign in, and their scheduled posts are paused. Their data is kept, and you can reactivate them at any time.`,
  },
  reactivate: {
    label: 'Reactivate', btn: 'btn-primary', reason: null,
    describe: (u) => `${u.name} can sign in again and gets an email. Paused scheduled posts resume — any whose time has already passed will publish straight away.`,
  },
  'logout-all': {
    label: 'Sign out all devices', btn: 'btn-outline', reason: null,
    describe: (u) => `Ends every signed-in session for ${u.name}. They can sign in again straight away.`,
  },
  'verify-email': {
    label: 'Mark email verified', btn: 'btn-outline', reason: null,
    describe: (u) => `Treat ${u.email} as verified without the user clicking the link. Only do this if you know the address is theirs.`,
  },
  'resend-verification': {
    label: 'Resend verification', btn: 'btn-outline', reason: null,
    describe: (u) => `Send a new verification link to ${u.email}.`,
  },
  delete: {
    label: 'Delete', btn: 'btn-danger', reason: null,
    describe: (u) => (u.org_role === 'owner'
      ? `${u.name} owns their organization, so this deletes the whole organization: every member, and all posts, connected social accounts, groups and media. This can't be undone.`
      : `Permanently deletes ${u.name}'s sign-in. Posts, accounts and groups they created stay with their organization.`),
  },
};

export function availableActions(u) {
  const byStatus = {
    pending: ['approve', 'reject'],
    active: ['suspend', 'deactivate', 'logout-all'],
    suspended: ['reactivate', 'deactivate'],
    deactivated: ['reactivate'],
    rejected: ['approve'],
  }[u.status] || [];
  const email = u.email_verified_at ? [] : ['resend-verification', 'verify-email'];
  return [...byStatus, ...email, 'delete'];
}

// Confirms one action (asking for a reason or the typed email where needed) and runs it.
export function ActionDialog({ user, action, onClose, onDone }) {
  const cfg = ACTIONS[action];
  const [reason, setReason] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const fetchAdminPending = useStore(s => s.fetchAdminPending);

  const canSubmit = !busy
    && (cfg.reason !== 'required' || reason.trim())
    && (action !== 'delete' || confirmEmail.trim().toLowerCase() === user.email.toLowerCase());

  const submit = async () => {
    setBusy(true);
    try {
      if (action === 'delete') await adminAPI.deleteUser(user.id, confirmEmail.trim());
      else await adminAPI.act(user.id, action, cfg.reason ? { reason: reason.trim() || null } : undefined);
      toast.success(action === 'delete' ? `${user.name} was deleted` : `${cfg.label}: done for ${user.name}`);
      fetchAdminPending();
      onDone(action);
    } catch (err) {
      toast.error(errorOf(err, `Could not ${cfg.label.toLowerCase()}`));
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={e => { e.stopPropagation(); if (!busy) onClose(); }}>
      <div className="modal" style={{ width: 440, maxWidth: 'calc(100vw - 32px)' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><div className="modal-title">{cfg.label} — {user.name}</div></div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: '#4a5568', lineHeight: 1.6, marginBottom: 14 }}>{cfg.describe(user)}</p>
          {cfg.reason && (
            <div className="form-group">
              <label className="form-label">Reason {cfg.reason === 'required' ? <span className="req">*</span> : <span style={{ fontWeight: 400, color: '#8892a4' }}>(optional — included in the email)</span>}</label>
              <textarea className="form-textarea" rows={3} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
            </div>
          )}
          {action === 'delete' && (
            <div className="form-group">
              <label className="form-label">Type <strong>{user.email}</strong> to confirm</label>
              <input className="form-input" value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} autoFocus />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" disabled={busy} onClick={onClose}>Cancel</button>
          <button className={`btn ${cfg.btn === 'btn-outline' ? 'btn-primary' : cfg.btn}`} disabled={!canSubmit} onClick={submit}>
            {busy ? 'Working…' : cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#8892a4', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#1a1f2e' }}>{children}</div>
    </div>
  );
}

// Full details of one user with every available action.
export function UserDetailModal({ userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null);

  const load = () => adminAPI.user(userId)
    .then(r => setData(r.data))
    .catch(err => { toast.error(errorOf(err, 'Could not load this user')); onClose(); });

  useEffect(() => { load(); }, [userId]);

  const done = (a) => {
    setAction(null);
    onChanged();
    if (a === 'delete') onClose();
    else load();
  };

  const u = data?.user;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 600, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{u ? u.name : 'User'}</div>
          <span style={{ cursor: 'pointer', color: '#8892a4', fontSize: 18 }} onClick={onClose}>×</span>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {!data ? <div className="loading-center" style={{ padding: 30 }}><div className="spinner" /></div> : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#4a5568' }}>{u.email}</span>
                <StatusBadge user={u} />
              </div>
              {data.organization && (
                <div style={{ fontSize: 12, color: '#4a5568', marginBottom: 16 }}>
                  <span style={{ textTransform: 'capitalize' }}>{u.org_role}</span> of{' '}
                  <Link to={`/admin/organizations?status=all&org=${data.organization.id}`} style={{ color: '#0f766e', fontWeight: 600 }}>{data.organization.name}</Link>{' '}
                  <span className={`badge badge-${data.organization.status}`}>{STATUS_LABEL[data.organization.status]}</span>
                  {data.organization.status !== 'active' && <span style={{ color: '#8892a4' }}> — no one in it can sign in until the organization is active</span>}
                </div>
              )}
              {u.status_reason && (
                <div style={{ padding: '8px 12px', background: '#f8f9fb', border: '1px solid #e8eaf0', borderRadius: 6, fontSize: 12, marginBottom: 16 }}>
                  <strong>Reason:</strong> {u.status_reason}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 18 }}>
                <Field label="Registered">{fmtDate(u.created_at)}</Field>
                <Field label="Email verified">{fmtDate(u.email_verified_at)}</Field>
                <Field label="Approved">{fmtDate(u.approved_at)}</Field>
                <Field label="Last sign-in">{fmtDate(u.last_login_at)}</Field>
                <Field label="Status changed">{fmtDate(u.status_changed_at)}</Field>
                <Field label="Timezone">{u.timezone || '—'}</Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8, marginBottom: 18 }}>
                {[['Org social accounts', data.counts.accounts], ['Org posts', data.counts.posts], ['Org scheduled', data.counts.queued_posts],
                  ['Org groups', data.counts.groups], ['Org media files', data.counts.media_files], ['Signed-in devices', data.counts.active_sessions]].map(([label, n]) => (
                  <div key={label} className="card" style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{n}</div>
                    <div style={{ fontSize: 10, color: '#8892a4' }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
                {availableActions(u).map(a => (
                  <button key={a} className={`btn ${ACTIONS[a].btn}`} onClick={() => setAction(a)}>{ACTIONS[a].label}</button>
                ))}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>History</div>
              {data.history.length === 0 ? <div style={{ fontSize: 12, color: '#8892a4' }}>No admin actions yet.</div> : (
                <table className="data-table">
                  <tbody>
                    {data.history.map(h => (
                      <tr key={h.id}>
                        <td style={{ width: 'auto' }}>
                          <div style={{ fontWeight: 600 }}>{ACTION_LOG_LABEL[h.action] || h.action}</div>
                          {h.reason && <div style={{ color: '#4a5568', marginTop: 2 }}>{h.reason}</div>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', color: '#8892a4', textAlign: 'right' }}>{fmtDate(h.created_at)}{h.admin_name ? ` · ${h.admin_name}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
      {action && u && <ActionDialog user={u} action={action} onClose={() => setAction(null)} onDone={done} />}
    </div>
  );
}
